import apiClient from '../client'
import type { OreOriginHeatmapResponse } from '../models/response/oreOriginHeatmap'
import type { SupplierRiskRankingResponse } from '../models/response/supplierRisk'
import type {
  CashProjectionResponse,
  CashProjectionConfig,
  CashProjectionPrice,
  CashProjectionSnapshotListItem,
  UpdateCashProjectionConfigBody,
  SetCommodityPriceBody,
} from '../models/response/cashProjection'
import type {
  LaborStabilityResponse,
  LaborStabilityFiltersResponse,
  LaborStabilityConfig,
  LaborStabilitySnapshotListItem,
  LaborStabilityQuery,
  UpdateLaborStabilityConfigBody,
  AppendLaborStabilityToExpedienteResult,
} from '../models/response/laborStability'
import type {
  BottleneckResponse,
  BottleneckConfig,
  BottleneckSnapshotListItem,
  BottleneckQuery,
  UpdateBottleneckConfigBody,
} from '../models/response/bottleneck'
import type {
  SustainabilityReportResponse,
  SustainabilitySnapshotListItem,
  SustainabilityQuery,
  SustainabilityConfig,
} from '../models/response/sustainability'

const BASE_PATH = '/analytics'

// Construye el query-string para los endpoints POST que reciben los filtros del
// reporte vía [FromQuery] (snapshot / expediente).
function laborQs(q: LaborStabilityQuery): string {
  const p = new URLSearchParams()
  p.set('miningRightId', q.miningRightId)
  if (q.mineralTypeId) p.set('mineralTypeId', q.mineralTypeId)
  if (q.componentId) p.set('componentId', q.componentId)
  if (q.dateFrom) p.set('dateFrom', q.dateFrom)
  if (q.dateTo) p.set('dateTo', q.dateTo)
  if (q.windowSize != null) p.set('windowSize', String(q.windowSize))
  return `?${p.toString()}`
}

// Query-string para los endpoints POST de Cuellos de Botella que reciben el
// rango de fechas vía [FromQuery] (snapshot).
function bottleneckQs(q?: BottleneckQuery): string {
  const p = new URLSearchParams()
  if (q?.dateFrom) p.set('dateFrom', q.dateFrom)
  if (q?.dateTo) p.set('dateTo', q.dateTo)
  const s = p.toString()
  return s ? `?${s}` : ''
}

// Query-string para los endpoints POST de Sostenibilidad (snapshot/recálculo).
function sustainabilityQs(q?: SustainabilityQuery): string {
  const p = new URLSearchParams()
  if (q?.dateFrom) p.set('dateFrom', q.dateFrom)
  if (q?.dateTo) p.set('dateTo', q.dateTo)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const analyticsService = {
  async oreOriginHeatmap(params?: {
    dateFrom?: string
    dateTo?: string
    revealSuppliers?: boolean
  }): Promise<OreOriginHeatmapResponse> {
    return apiClient.get<OreOriginHeatmapResponse>(`${BASE_PATH}/ore-origin-heatmap`, params)
  },

  async supplierRiskRanking(): Promise<SupplierRiskRankingResponse> {
    return apiClient.get<SupplierRiskRankingResponse>(`${BASE_PATH}/supplier-risk`)
  },

  // ── Proyección de Caja (req. 1.7) ──
  async cashProjection(): Promise<CashProjectionResponse> {
    return apiClient.get<CashProjectionResponse>(`${BASE_PATH}/cash-projection`)
  },

  async updateCashProjectionConfig(
    body: UpdateCashProjectionConfigBody,
  ): Promise<CashProjectionConfig> {
    return apiClient.put<CashProjectionConfig, UpdateCashProjectionConfigBody>(
      `${BASE_PATH}/cash-projection/config`,
      body,
    )
  },

  async refreshCashPrices(): Promise<number> {
    return apiClient.post<number>(`${BASE_PATH}/cash-projection/prices/refresh`)
  },

  async setCashPrice(body: SetCommodityPriceBody): Promise<CashProjectionPrice> {
    return apiClient.put<CashProjectionPrice, SetCommodityPriceBody>(
      `${BASE_PATH}/cash-projection/prices`,
      body,
    )
  },

  async saveCashSnapshot(): Promise<CashProjectionSnapshotListItem> {
    return apiClient.post<CashProjectionSnapshotListItem>(`${BASE_PATH}/cash-projection/snapshot`)
  },

  // ── Estabilidad de la Labor (req. 1.7) ──
  async laborStability(q: LaborStabilityQuery): Promise<LaborStabilityResponse> {
    return apiClient.get<LaborStabilityResponse>(`${BASE_PATH}/labor-stability`, {
      miningRightId: q.miningRightId,
      mineralTypeId: q.mineralTypeId,
      componentId: q.componentId,
      dateFrom: q.dateFrom,
      dateTo: q.dateTo,
      windowSize: q.windowSize,
    })
  },

  async laborStabilityFilters(): Promise<LaborStabilityFiltersResponse> {
    return apiClient.get<LaborStabilityFiltersResponse>(`${BASE_PATH}/labor-stability/filters`)
  },

  async getLaborStabilityConfig(): Promise<LaborStabilityConfig> {
    return apiClient.get<LaborStabilityConfig>(`${BASE_PATH}/labor-stability/config`)
  },

  async updateLaborStabilityConfig(
    body: UpdateLaborStabilityConfigBody,
  ): Promise<LaborStabilityConfig> {
    return apiClient.put<LaborStabilityConfig, UpdateLaborStabilityConfigBody>(
      `${BASE_PATH}/labor-stability/config`,
      body,
    )
  },

  async saveLaborStabilitySnapshot(q: LaborStabilityQuery): Promise<LaborStabilitySnapshotListItem> {
    return apiClient.post<LaborStabilitySnapshotListItem>(
      `${BASE_PATH}/labor-stability/snapshot${laborQs(q)}`,
    )
  },

  async appendLaborStabilityToExpediente(
    q: LaborStabilityQuery,
  ): Promise<AppendLaborStabilityToExpedienteResult> {
    return apiClient.post<AppendLaborStabilityToExpedienteResult>(
      `${BASE_PATH}/labor-stability/expediente${laborQs(q)}`,
    )
  },

  // URL absoluta del PDF (descarga autenticada manual desde la página).
  laborStabilityPdfUrl(q: LaborStabilityQuery): string {
    return apiClient.buildUrl(`${BASE_PATH}/labor-stability/pdf`, {
      miningRightId: q.miningRightId,
      mineralTypeId: q.mineralTypeId,
      componentId: q.componentId,
      dateFrom: q.dateFrom,
      dateTo: q.dateTo,
      windowSize: q.windowSize,
    })
  },

  // ── Cuellos de Botella / Lead Time (req. 1.7) ──
  async bottleneck(q?: BottleneckQuery): Promise<BottleneckResponse> {
    return apiClient.get<BottleneckResponse>(`${BASE_PATH}/bottlenecks`, {
      dateFrom: q?.dateFrom,
      dateTo: q?.dateTo,
    })
  },

  async getBottleneckConfig(): Promise<BottleneckConfig> {
    return apiClient.get<BottleneckConfig>(`${BASE_PATH}/bottlenecks/config`)
  },

  async updateBottleneckConfig(body: UpdateBottleneckConfigBody): Promise<BottleneckConfig> {
    return apiClient.put<BottleneckConfig, UpdateBottleneckConfigBody>(
      `${BASE_PATH}/bottlenecks/config`,
      body,
    )
  },

  async saveBottleneckSnapshot(q?: BottleneckQuery): Promise<BottleneckSnapshotListItem> {
    return apiClient.post<BottleneckSnapshotListItem>(`${BASE_PATH}/bottlenecks/snapshot${bottleneckQs(q)}`)
  },

  bottleneckPdfUrl(q?: BottleneckQuery): string {
    return apiClient.buildUrl(`${BASE_PATH}/bottlenecks/pdf`, {
      dateFrom: q?.dateFrom,
      dateTo: q?.dateTo,
    })
  },

  // ── Sostenibilidad / Huella de Carbono (req. 1.7) ──
  async sustainability(q?: SustainabilityQuery): Promise<SustainabilityReportResponse> {
    return apiClient.get<SustainabilityReportResponse>(`${BASE_PATH}/sustainability`, {
      dateFrom: q?.dateFrom,
      dateTo: q?.dateTo,
    })
  },

  async recalculateSustainability(q?: SustainabilityQuery): Promise<number> {
    return apiClient.post<number>(`${BASE_PATH}/sustainability/recalculate${sustainabilityQs(q)}`)
  },

  async saveSustainabilitySnapshot(q?: SustainabilityQuery): Promise<SustainabilitySnapshotListItem> {
    return apiClient.post<SustainabilitySnapshotListItem>(
      `${BASE_PATH}/sustainability/snapshot${sustainabilityQs(q)}`,
    )
  },

  sustainabilityPdfUrl(q?: SustainabilityQuery): string {
    return apiClient.buildUrl(`${BASE_PATH}/sustainability/pdf`, {
      dateFrom: q?.dateFrom,
      dateTo: q?.dateTo,
    })
  },

  async getSustainabilityConfig(): Promise<SustainabilityConfig> {
    return apiClient.get<SustainabilityConfig>(`${BASE_PATH}/sustainability/config`)
  },

  async updateSustainabilityConfig(body: SustainabilityConfig): Promise<SustainabilityConfig> {
    return apiClient.put<SustainabilityConfig, SustainabilityConfig>(
      `${BASE_PATH}/sustainability/config`,
      body,
    )
  },

  // ── Getter de config de Proyección de Caja (para la pantalla central) ──
  async getCashProjectionConfig(): Promise<CashProjectionConfig> {
    return apiClient.get<CashProjectionConfig>(`${BASE_PATH}/cash-projection/config`)
  },
}
